// @flow
import React, { Component, Fragment } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage, defineMessages, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Default, Desktop, Mobile } from '../../../utils/responsive';
import { freeShippingCap } from '../../local-values/utils/benefits';
import { getLocalizedCountry } from '../../local-values/utils/countries';
import MissingForMaxValue from '../../../elements/MissingForMaxValue/MissingForMaxValue';

import Loading from '../../../elements/Loading/Loading';
import {deleteVirtualShoppingCart} from '../duck';
import style from './ShoppingCartMenu.scss';
import navbarStyle from '../../navbar/components/Navbar.scss';
import icons from '../../../assets/styles/icons.scss';
import buttonStyle from '../../../assets/styles/button.scss';

import type { HTTPError } from '../../../error';
import type { ShoppingCartItem, User } from '../../../entities';
import type { LocalValuesState } from '../../local-values/duck';
import type { LocaleState } from '../../locale/duck';
import type { Element } from 'react';
import type { VirtualItem } from '../api';

type Props = {
  +closeShoppingCartMenu: () => void;
  +deleteVirtualCartItem: (index: number) => void;
  +deleteItem: (itemNumber: number) => void;
  +deleteItemError?: HTTPError;
  +fetchCartError?: HTTPError;
  +fetchShoppingCart: () => void;
  +fetchVirtualShoppingCart: () => void;
  +deleteVirtualShoppingCart: () => void;
  +isDeletingItem?: boolean;
  +isFetchingCart?: boolean;
  +goToRedirect: (string) => void;
  +open: boolean;
  +shoppingCart?: ShoppingCartItem[];
  +virtualShoppingCart: VirtualItem[];
  +localValues: LocalValuesState;
  +locale: LocaleState;
  +user?: User;
  +authenticated?: boolean;
};

const messages = defineMessages({
  moreForFreeOne: {
    id: 'newOrder.freeShippingBanner.moreForFree.one',
  },
  moreForFreeTwo: {
    id: 'newOrder.freeShippingBanner.moreForFree.two',
  },
  moreForFreeThree: {
    id: 'newOrder.freeShippingBanner.moreForFree.three',
  },
  moreForFreeFour: {
    id: 'newOrder.freeShippingBanner.moreForFree.four',
  },
  itsFreeOne: {
    id: 'newOrder.freeShippingBanner.itsFree.one',
  },
  itsFreeTwo: {
    id: 'newOrder.freeShippingBanner.itsFree.two',
  },
  free: {
    id: 'newOrder.freeShippingBanner.free',
  },
  error: {
    id: 'newOrder.freeShippingBanner.error',
  },
});

export default class ShoppingCartMenu extends Component<Props> {

  componentDidMount() {
    document.addEventListener('click', this.handleClickOutside.bind(this));
    const {
      fetchShoppingCart, shoppingCart, deleteVirtualShoppingCart, authenticated, user
    } = this.props;
    if (!authenticated && !user) {
      // fetchVirtualShoppingCart();
      deleteVirtualShoppingCart();
    } else if (typeof shoppingCart === 'undefined') {
      fetchShoppingCart();
    }
  }

  componentWillUnmount() {
    // Ensure to remove listener if loggedout with menu open
    document.removeEventListener('click', this.handleClickOutside.bind(this));
  }

  handleClickOutside(e: Event) {
    const { closeShoppingCartMenu } = this.props;
    const wrap = document.querySelector(`.${style.shoppingCartMenuWrap}`);
    const mainWrap = document.querySelector(`.${navbarStyle.shoppingCartButton}`);
    const addToCartWrap = document.getElementById('addToCartButton');
    if (location.pathname.split('/')[2] !== 'product') {
      if (wrap && mainWrap && e.target instanceof Node) {
        const target = e.target;
        // NOTE: the target must be available in DOM, otherwise it might has been dynamically deleted
        // NOTE: (in which case, we don't want to close the menu)
        if (!mainWrap.contains(target) && !wrap.contains(target) && document.body && document.body.contains(target)) {
          closeShoppingCartMenu();
        }
      }
    } else {
      if (wrap && mainWrap && addToCartWrap && e.target instanceof Node) {
        const target = e.target;
        // NOTE: the target must be available in DOM, otherwise it might has been dynamically deleted
        // NOTE: (in which case, we don't want to close the menu)
        if (!mainWrap.contains(target) && !wrap.contains(target) && !addToCartWrap.contains(target) &&
          document.body && document.body.contains(target)) {
          closeShoppingCartMenu();
        }
      }
    }
  }

  handleClickOutsideResponsive() {
    const { closeShoppingCartMenu } = this.props;
    closeShoppingCartMenu();
  }

  handleClickRemoveItem(itemNumber: number, index: number) {
    const { deleteItem, authenticated, user, deleteVirtualCartItem } = this.props;
    if (!authenticated || !user) {
      return deleteVirtualCartItem(index);
    }
    deleteItem(itemNumber);
  }

  handleClickQuote() {
    const { goToRedirect, closeShoppingCartMenu, user, authenticated } = this.props;
    if (!!user && !!authenticated) {
      goToRedirect(`/new-order/quote/cart`);
    } else {
      goToRedirect(`/new-order/quote/offline-cart`);
    }
    closeShoppingCartMenu();
  }

  buildFreeShippingMessage(country: string, total: number, isLoading: boolean): Element<'section'> {
    const { locale } = this.props;
    const amountMissing = <strong>${(freeShippingCap - total).toFixed(2)}</strong>;
    const free = <strong>{this.context.intl.formatMessage(messages.free)}</strong>;
    let message;

    if (['PE', 'CO'].includes(locale.domainCountry)) {
      if (total < freeShippingCap) {
        message = <p>{`${this.context.intl.formatMessage(messages.moreForFreeOne)} `} {amountMissing}
          {` ${this.context.intl.formatMessage(messages.moreForFreeTwo)} `} {free}
          {` ${this.context.intl.formatMessage(messages.moreForFreeThree)} ${country} `}</p>;
      } else {
        message = <p>{`${this.context.intl.formatMessage(messages.itsFreeOne)} `} {free}
          {` ${this.context.intl.formatMessage(messages.itsFreeTwo)} ${country}`}</p>;
      }
    } else if (locale.domainCountry === 'US') {
      if (total < freeShippingCap) {
        message = <p>{`${this.context.intl.formatMessage(messages.moreForFreeOne)} `} {amountMissing}
          {` ${this.context.intl.formatMessage(messages.moreForFreeTwo)} `} {free}
          {` ${this.context.intl.formatMessage(messages.moreForFreeThree)} ${country}.`}
          {` ${this.context.intl.formatMessage(messages.moreForFreeFour)}`}</p>;
      } else {
        message = <p>{`${this.context.intl.formatMessage(messages.itsFreeOne)} `} {free}
          {` ${this.context.intl.formatMessage(messages.itsFreeTwo)} ${country}`}</p>;
      }
    } else {
      message = <p>{this.context.intl.formatMessage(messages.error)}</p>;
    }

    return (
      <section className={style.freeShippingBlock}>
        {!isLoading && message}
      </section>);
  }

  getShoppingCart() {
    const { user, authenticated, virtualShoppingCart, shoppingCart } = this.props;
    if (!user || !authenticated) {
      return virtualShoppingCart;
    }
    return shoppingCart;
  }

  render() {
    const {
      open, closeShoppingCartMenu, fetchCartError, shoppingCart, isFetchingCart, isDeletingItem,
      localValues, locale, user, authenticated, virtualShoppingCart,
    } = this.props;
    const country = locale && getLocalizedCountry(locale.domainCountry || 'PE', this.context.intl.formatMessage, localValues.countriesMap);
    const isCartEmpty = (!!user && !!authenticated || !virtualShoppingCart.length)
      && (!shoppingCart || !shoppingCart.length);
    let itemTotal = 0;
    let subtotal = 0;
    let shipping = 0;
    const cart = this.getShoppingCart();
    const items = !!cart && cart.map((item: ShoppingCartItem, n: number) => {
      // TODO: CHANGEME
      itemTotal = itemTotal + parseFloat(item.price) * item.amount;
      const parsedShipping = parseFloat(item.shipping);
      subtotal = itemTotal + parsedShipping;
      shipping = shipping + (isNaN(parsedShipping) ? 0 : parsedShipping);
      return (
        <li key={n}>
          <div className={style.imageWrapper}>
            <div className={style.amount}>
              <p>{item.amount}</p>
            </div>
            <img className={style.itemImage}
                 src={item.image || item.imageURI} alt="Order image"/>
          </div>
          <section className={style.title}>
            <p>{item.name}</p>
          </section>
          <p className={style.price}>$ {item.price}</p>
          <button className={style.remove}
                  onClick={() => this.handleClickRemoveItem(item.number, n)}>
            <i className={classNames(icons.iconTrashcanMedium)}/>
          </button>
        </li>);
    });
    const emptyCart = isCartEmpty && !isFetchingCart && !fetchCartError && (
      <FormattedMessage id="nav.shoppingCartMenu.noResults">{
        txt => <p className={style.noResult}>{txt}</p>
      }</FormattedMessage>
    );

    const loading = (isFetchingCart || isDeletingItem) && <Loading type="circle"
                                                                   className={style.loading}/>;

    const error = fetchCartError && (
      <FormattedMessage id="nav.shoppingCartMenu.fetchError">{
        txt => <p className={style.noResult}>{txt}</p>
      }</FormattedMessage>);

    const list = <ul className={classNames({ [style.disabled]: isDeletingItem })}>
      {items}
      {error}
    </ul>;

    const content = (
      <section className={style.productContent}>
        {list}
        {!!isCartEmpty && emptyCart}
        {!!loading && loading}
        {!isCartEmpty && <Fragment>
          <section className={classNames(style.totalWrap, { [style.disabled]: isDeletingItem })}>
            <FormattedMessage id="nav.shoppingCartMenu.priceAt">{
              txt => <p className={style.left}>{txt}</p>
            }</FormattedMessage>
            <p className={style.right}>$ {itemTotal.toFixed(2)}</p>
          </section>
          <section className={classNames(style.shippingWrap, { [style.disabled]: isDeletingItem })}>
            <FormattedMessage id="nav.shoppingCartMenu.shipping">{
              txt => <p className={style.left}>{txt}</p>
            }</FormattedMessage>
            <p className={style.right}>$ {shipping.toFixed(2)}</p>
          </section>
          {typeof subtotal === 'number' && subtotal < 40 && <section className={style.MissingForMaxValueWrap}>
            <MissingForMaxValue amount={subtotal}/>
          </section>}
          {/*this.buildFreeShippingMessage(country, itemTotal, !!(isDeletingItem || isFetchingCart))*/}
          <FormattedMessage id="nav.shoppingCartMenu.quote">{
            txt => <button
              className={classNames(buttonStyle.btn, buttonStyle.lightBlue, style.quote,
                { [style.disabled]: isDeletingItem })}
              onClick={this.handleClickQuote.bind(this)}>
              <i className={icons.iconShoppingCartMedium}/>
              {txt}
            </button>
          }</FormattedMessage>
          <FormattedMessage id="nav.shoppingCartMenu.close">{
            txt => <button className={classNames(style.close, { [style.disabled]: isDeletingItem })}
                           onClick={_ => closeShoppingCartMenu()}>{txt}</button>
          }</FormattedMessage>
        </Fragment>}
      </section>
    );

    const responsiveMenu = open && <Fragment>
      <aside key="responsiveMenu"
             className={style.shoppingCartResponsiveMenuWrap}>
        {content}
      </aside>
    </Fragment>;

    return (
      <Fragment>
        {open && <Helmet key="helmet">
          <style type="text/css">{`
            body {
              overflow: hidden!important;
            }
        `}</style>
        </Helmet>}
        <Default>
          <aside className={classNames(style.shoppingCartMenuWrap, { [style.loggedOn]: (!!authenticated && !!user) },
            { [style.open]: open })}>
            {content}
          </aside>
        </Default>
        <Mobile>
          <section onClick={this.handleClickOutsideResponsive.bind(this)}
                   className={classNames(style.veil, { [style.veilOpen]: open })}/>
          {responsiveMenu}
        </Mobile>
      </Fragment>
    )
  }
}

ShoppingCartMenu.propTypes = {
  closeShoppingCartMenu: PropTypes.func.isRequired,
  deleteVirtualCartItem: PropTypes.func.isRequired,
  deleteItem: PropTypes.func.isRequired,
  deleteItemError: PropTypes.object,
  fetchCartError: PropTypes.object,
  fetchShoppingCart: PropTypes.func.isRequired,
  fetchVirtualShoppingCart: PropTypes.func.isRequired,
  deleteVirtualShoppingCart: PropTypes.func.isRequired,
  isDeletingItem: PropTypes.bool,
  isFetchingCart: PropTypes.bool,
  goToRedirect: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  shoppingCart: PropTypes.array,
  virtualShoppingCart: PropTypes.array.isRequired,
  localValues: PropTypes.object.isRequired,
  locale: PropTypes.object.isRequired,
  user: PropTypes.object,
  authenticated: PropTypes.bool,
};

ShoppingCartMenu.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/shopping-cart/components/ShoppingCartMenu.jsx